'use client'

import { useQuery } from '@apollo/client/react'
import { ReactElement, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { Swiper, SwiperSlide } from 'swiper/react'

import { LmfAnalyticsProvider, LoadingSpinner, untilMd } from '@syconium/little-miss-figgy'

import { gql } from '../../../../__generated__/graphql/catalog/gql'
import { FETCH_UPSELL_CONFIGURATION } from '../../../../components/pages/cart/MiniCart/Upsells/Upsells'
import { isValidNostoPlacementId } from '../../../../types/nosto'
import { useFetchNostoPlacementData } from '../../../_hooks/useFetchNostoPlacementData'
import { useCart } from '../../../_providers/CartProvider.client'
import { useLocalization } from '../../../_providers/LocalizationProvider.client'
import { useTranslation } from '../../../_providers/TranslationProvider.client'

import { CarouselControls } from './CarouselControls'
import { CartInterstitialUpsellTile } from './CartInterstitialUpsellTile'
import { Body, LoadingSpinnerWrapper, SwiperWrapper, Title, TitleWrapper } from './styles'

import type { Swiper as SwiperInstance } from 'swiper'

const MAX_UPSELL_ITEMS = 3
const DEFAULT_NOSTO_PLACEMENT_ID = 'cartpage-nosto-4'

const FETCH_CART_INTERSTITIAL_UPSELL_PRODUCTS = gql(`
  query GetVariantsForCartInterstitialUpsell($skusForVariantGroups: [String!]) {
    productCatalogVariants(skus: $skusForVariantGroups) {
      ...CartInterstitialUpsellVariant
    }
  }
`)

export const CartInterstitialUpsells = (): ReactElement | null => {
  const swiperRef = useRef<SwiperInstance | null>(null)
  const { region } = useLocalization()
  const { cart } = useCart()
  const {
    pages: {
      product: { productRecommendations },
    },
    magnolia: {
      general: { loading: loadingString },
    },
  } = useTranslation()

  const { data: upsellConfigData } = useQuery(FETCH_UPSELL_CONFIGURATION, {
    variables: { regionId: region.id },
  })

  const { nostoUpsellId: requestedNostoPlacementId, upsellSkus: defaultUpsellSkus } =
    upsellConfigData?.siteMessages?.miniCartMessages ?? {}

  const nostoEnabled = !!requestedNostoPlacementId

  const nostoPlacementId = isValidNostoPlacementId(requestedNostoPlacementId)
    ? requestedNostoPlacementId
    : DEFAULT_NOSTO_PLACEMENT_ID

  const {
    data: { skusForVariantGroups: nostoUpsellSkus },
    status: nostoStatus,
  } = useFetchNostoPlacementData(nostoPlacementId)

  const skusForVariantGroups = useMemo(() => {
    return (nostoEnabled ? nostoUpsellSkus : [])
      .concat(defaultUpsellSkus ?? [])
      .slice(0, MAX_UPSELL_ITEMS * 2) // fetch extra to absorb cart-item filtering
  }, [defaultUpsellSkus, nostoEnabled, nostoUpsellSkus])

  const { data, loading: fetchLoading } = useQuery(FETCH_CART_INTERSTITIAL_UPSELL_PRODUCTS, {
    skip: skusForVariantGroups.length === 0,
    variables: { skusForVariantGroups },
  })

  const cartSkus = useMemo(() => new Set(Object.values(cart.items).map(i => i.sku)), [cart.items])

  const variants = useMemo(() => {
    return (data?.productCatalogVariants ?? [])
      .filter(v => !cartSkus.has(v.sku))
      .slice(0, MAX_UPSELL_ITEMS)
  }, [data?.productCatalogVariants, cartSkus])

  const loading = fetchLoading || (nostoEnabled && nostoStatus === 'pending')

  if (!loading && variants.length === 0) return null

  return (
    <Body>
      <TitleWrapper>
        <Title>{productRecommendations.title}</Title>
      </TitleWrapper>
      {loading ? (
        <LoadingSpinnerWrapper>
          <LoadingSpinner label={loadingString} size='24px' />
        </LoadingSpinnerWrapper>
      ) : (
        <SwiperWrapper>
          <DesktopOnly>
            <CarouselControls swiperRef={swiperRef} />
          </DesktopOnly>
          <LmfAnalyticsProvider title='Cart Interstitial Upsells Slider'>
            <Swiper
              onSwiper={swiper => (swiperRef.current = swiper)}
              slidesPerView='auto'
              spaceBetween={12}
              observer
              observeParents
            >
              {variants.map(variant => (
                <SwiperSlide key={variant.id}>
                  <LmfAnalyticsProvider title='Cart Interstitial Upsells Slide'>
                    <CartInterstitialUpsellTile variant={variant} />
                  </LmfAnalyticsProvider>
                </SwiperSlide>
              ))}
            </Swiper>
          </LmfAnalyticsProvider>
        </SwiperWrapper>
      )}
    </Body>
  )
}

const DesktopOnly = styled.div`
  ${untilMd} {
    display: none;
  }
`
